import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchTournamentInfo, fetchTeams, fetchTeamLogoUrl } from "../api";
import "./LandingPage.css";

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

function TeamCard({ team }) {
    const [logoUrl, setLogoUrl] = useState(null);
    const pct =
        team.max_players > 0
            ? Math.round((team.player_count / team.max_players) * 100)
            : 0;

    useEffect(() => {
        if (team.logo_url) {
            fetchTeamLogoUrl(team.id)
                .then((r) => setLogoUrl(r.data.url))
                .catch(() => {});
        }
    }, [team.id, team.logo_url]);

    return (
        <div className="team-card">
            <div className="team-initial">
                {logoUrl ? (
                    <img src={logoUrl} alt={team.name} />
                ) : (
                    team.name.charAt(0)
                )}
            </div>
            <div className="team-card-info">
                <strong>{team.name}</strong>
                <div className="team-bar-wrap">
                    <div className="team-bar">
                        <div
                            className="team-bar-fill"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <span className="team-bar-label">
                        {team.player_count}/{team.max_players}명
                    </span>
                </div>
            </div>
        </div>
    );
}

export default function LandingPage() {
    const [tournament, setTournament] = useState(null);
    const [teams, setTeams] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchTournamentInfo()
            .then((r) => setTournament(r.data))
            .catch(() => {});
        fetchTeams()
            .then((r) => setTeams(r.data))
            .catch(() => {});
    }, []);

    const isClosed = tournament && !tournament.registration_open;
    const filledSpots = teams.reduce((a, t) => a + (t.player_count || 0), 0);

    const calcDDay = (isoDate) => {
        if (!isoDate) return null;
        const diff = new Date(isoDate + "T00:00:00") - new Date();
        return Math.ceil(diff / (1000 * 60 * 60 * 24));
    };
    const regDDay = calcDDay(tournament?.registration_deadline);
    const tournamentDDay = calcDDay(tournament?.tournament_date);

    return (
        <div className="landing">
            {/* Nav */}
            <nav className="landing-nav">
                <div className="nav-emblem">
                    <img
                        src="/images/kasausa_icon.png"
                        alt="KFAUS"
                        className="nav-logo-img"
                    />
                    <div className="nav-org">
                        재미대한축구협회
                        <span>Korean-american soccer association</span>
                    </div>
                </div>
                <div className="nav-spacer" />
            </nav>

            {/* Hero */}
            <section className="hero">
                <div className="hero-content">
                    <div className="hero-text">
                        <div
                            className={`hero-eyebrow ${isClosed ? "closed" : ""}`}
                        >
                            <div className="hero-eyebrow-dot" />
                            {isClosed
                                ? "등록 마감 · Registration Closed"
                                : "선수 등록 모집 중 · Now Recruiting"}
                        </div>
                        <h1 className="hero-title">
                            {/* {tournament?.name ? (
                                tournament.name.replace(
                                    "협회장 배",
                                    "협회장\u00A0배",
                                )
                            ) : (
                                <>
                                    제
                                    <span className="hero-title-accent">
                                        24
                                    </span>
                                    회<br />
                                    재미대한축구협회장&nbsp;배
                                </>
                            )} */}
                            제 <span className="hero-title-accent">24</span>회
                            <br />
                            재미대한축구협회장&nbsp;배
                            <br />
                            전미 축구대회
                        </h1>
                        <div className="hero-subtitle">
                            The 24th KASAUSA National Soccer Tournament
                        </div>
                        <p className="hero-desc">
                            재미 한인 축구 선수 여러분의 많은 참여 바랍니다.
                            <br />
                            <span className="hero-desc-en">
                                We welcome all Korean-American soccer players to
                                participate.
                            </span>
                        </p>
                        <div className="hero-cta-row">
                            {isClosed ? (
                                <button
                                    className="btn-register disabled"
                                    disabled
                                >
                                    등록 마감 · Closed
                                </button>
                            ) : (
                                <button
                                    className="btn-register"
                                    onClick={() => navigate("/register")}
                                >
                                    선수 등록하기 / Register →
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Info cards */}
                    <div className="hero-info">
                        {/* <div
                            className="hero-logo-bg"
                            style={{
                                backgroundImage: `url('/images/kasausa_logo_white.png')`,
                            }}
                        /> */}
                        {tournament?.tournament_date && (
                            <div className="info-card">
                                <div className="info-card-icon">
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="rgba(255,255,255,0.6)"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect
                                            x="3"
                                            y="4"
                                            width="18"
                                            height="18"
                                            rx="3"
                                        />
                                        <path d="M16 2v4M8 2v4M3 10h18" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="info-card-label">
                                        대회 일자 <br /> Tournament Date
                                    </div>
                                    <div className="info-card-value">
                                        {formatDate(tournament.tournament_date)}
                                    </div>
                                </div>
                            </div>
                        )}
                        {tournament?.registration_fee != null && (
                            <div className="info-card">
                                <div className="info-card-icon">
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="rgba(255,255,255,0.6)"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M12 7v10M9.5 9.5C9.5 8.4 10.6 7.5 12 7.5s2.5.9 2.5 2c0 2.5-5 2.5-5 5 0 1.1 1.1 2 2.5 2s2.5-.9 2.5-2" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="info-card-label">
                                        등록비 <br /> Registration Fee
                                    </div>
                                    <div className="info-card-value open">
                                        ${tournament.registration_fee} / player
                                    </div>
                                </div>
                            </div>
                        )}
                        {tournament?.registration_deadline && (
                            <div className="info-card">
                                <div className="info-card-icon">
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="rgba(255,255,255,0.6)"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M12 7v5l3 3" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="info-card-label">
                                        접수 마감 <br /> Deadline
                                    </div>
                                    <div
                                        className={`info-card-value ${isClosed ? "closed" : "open"}`}
                                    >
                                        {isClosed
                                            ? "등록마감 · Closed"
                                            : formatDate(
                                                  tournament.registration_deadline,
                                              )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Stats */}
            {teams.length > 0 && (
                <div className="stats-bar">
                    <div className="stats-bar-inner">
                        <div className="stat">
                            <strong>{teams.length}</strong>
                            <span>참가팀 · Teams</span>
                        </div>
                        <div className="stat-divider" />
                        <div className="stat">
                            <strong>{filledSpots}</strong>
                            <span>등록 선수 · Players</span>
                        </div>
                        <div className="stat-divider" />
                        <div className="stat">
                            {isClosed ? (
                                <strong className="urgent">Closed</strong>
                            ) : regDDay !== null ? (
                                <strong
                                    className={regDDay <= 7 ? "urgent" : ""}
                                >
                                    D-{regDDay}
                                </strong>
                            ) : (
                                <strong>—</strong>
                            )}
                            <span>등록 마감 · Deadline</span>
                        </div>
                        <div className="stat-divider" />
                        <div className="stat">
                            {tournamentDDay !== null ? (
                                <strong>D-{tournamentDDay}</strong>
                            ) : (
                                <strong>—</strong>
                            )}
                            <span>대회까지 · D-Day</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Teams */}
            {teams.length > 0 && (
                <section className="teams-section">
                    <div className="section-inner">
                        <div className="section-header">
                            <h2 className="section-title">
                                참가 팀 · Participating Teams
                            </h2>
                        </div>
                        <div className="teams-grid">
                            {teams.map((team) => (
                                <TeamCard key={team.id} team={team} />
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            {!isClosed && (
                <section className="cta-section">
                    <div className="cta-inner">
                        <div className="cta-eyebrow">Join the Tournament</div>
                        <h2>지금 바로 등록하세요</h2>
                        <p>REGISTER NOW, BEFORE IT'S TOO LATE</p>
                        <button
                            className="btn-register"
                            onClick={() => navigate("/register")}
                        >
                            선수 등록하기 / Register →
                        </button>
                    </div>
                </section>
            )}

            <footer className="landing-footer">
                <span>
                    재미대한축구협회 / KOREAN-AMERICAN SOCCER ASSOCIATION © 2026
                </span>
                <a
                    href="/admin"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate("/admin");
                    }}
                >
                    Admin
                </a>
            </footer>
        </div>
    );
}
